import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { FiTrash, FiEdit, FiPlus, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import {  FaTimes } from 'react-icons/fa';
import LoadingElement from './loadingElement';

// Register the FilePond plugins
registerPlugin(FilePondPluginImagePreview);

const CardGrid = () => {
  const [cards, setCards] = useState([]);
  const [editCard, setEditCard] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [page, setPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return parseInt(params.get('page'), 10) || 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [animate, setAnimate] = useState(false);

  const addCard = async () => {
    const newCard = {
      title: `New Card Title ${cards.length + 1}`,
      description: 'This is a description for the new card.',
      imgSrc: '/uploads/mina.jpg',
    };
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'https://my-first-website-production.up.railway.app/cards',
        newCard,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCards([...cards, response.data]);
    } catch (error) {
      setIsLoading(false);
      console.error('Error adding card:', error);
    } finally {
      setIsLoading(false); // Set loading to false
    }
  };

  const deleteCard = async (id) => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://my-first-website-production.up.railway.app/cards/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCards(cards.filter(card => card._id !== id));
    } catch (error) {
      setIsLoading(false);
      console.error('Error deleting card:', error);
    } finally {
      setIsLoading(false); // Set loading to false
    }
  };

  const updateCard = async (id) => {
    let imageUrl = editCard.imgSrc;
    const token = localStorage.getItem('token');
    setIsLoading(true);

    if (files.length > 0) {
      const formData = new FormData();
      formData.append('image', files[0].file);

      try {
        const uploadResponse = await axios.post(
          'https://my-first-website-production.up.railway.app/upload',
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
          }
        );
        imageUrl = uploadResponse.data.imageUrl;
      } catch (error) {
        setIsLoading(false);
        console.error('Error uploading image:', error);
        return;
      }
    }

    const updatedCard = {
      title: editTitle,
      description: editDescription,
      imgSrc: imageUrl,
    };

    try {
      const response = await axios.put(
        `https://my-first-website-production.up.railway.app/cards/${id}`,
        updatedCard,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCards(cards.map(card => (card._id === id ? response.data : card)));
      setEditCard(null);
      setFiles([]);

    } catch (error) {
      setIsLoading(false);
      console.error('Error updating card:', error);
    } finally {
      setIsLoading(false); // Set loading to false
    }
  };

  const startEditCard = (card) => {
    setEditCard(card);
    setEditTitle(card.title);
    setEditDescription(card.description);
  };

  const handleImageClick = (card) => {
    setZoomedImage(card.imgSrc);
  };

  const closeZoom = () => {
    setZoomedImage(null);
  };

  const truncateDescription = (description) => {
    const maxLength = 100;
    return description.length > maxLength ? `${description.slice(0, maxLength)}...` : description;
  };
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const currentPage = parseInt(params.get('page'), 10);
    if (currentPage) {
      setPage(currentPage);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', page);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    const fetchCards = async () => {
      setIsLoading(true);

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://my-first-website-production.up.railway.app/cards', {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { page, limit: 6 },
        });
        setCards(response.data.cards);
        setTotalPages(response.data.totalPages);
        setAnimate(true);
      } catch (error) {
        console.error('Error fetching cards:', error);
      } finally {
        setIsLoading(false); // Set loading to false
      }
    };

    fetchCards();
  }, [page]);  
  const Pagination = ({ totalPages, currentPage, onPageChange }) => {
    const getVisiblePages = () => {
      let pages = [];
      if (totalPages <= 8) {
        pages = [...Array(totalPages).keys()].map(i => i + 1);
      } else {
        if (currentPage <= 4) {
          pages = [1, 2, 3, 4, 5, 'ellipsis1', totalPages];
        } else if (currentPage > 4 && currentPage < totalPages - 3) {
          pages = [1, 'ellipsis1', currentPage - 1, currentPage, currentPage + 1, 'ellipsis2', totalPages];
        } else {
          pages = [1, 'ellipsis1', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        }
      }
      return pages;
    };
        
    const visiblePages = getVisiblePages();

    return (
      <div className="flex pb-12 z-30 justify-center items-center space-x-2 mt-4">
        <button
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          className="px-2 py-1 rounded bg-green-400 dark:bg-gray-600"
        >
          <FiChevronLeft />
        </button>
        {visiblePages.map((page, index) =>
          page === 'ellipsis1' || page === 'ellipsis2' ? (
            <span key={page} className="px-2 py-1">
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-2 py-1 rounded ${currentPage === page ? 'bg-sky-400 dark:bg-blue-700 text-white' : 'bg-green-400 dark:bg-gray-600'}`}
            >
              {page}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          className="px-2 py-1 rounded bg-green-400 dark:bg-gray-600"
        >
          <FiChevronRight />
        </button>
      </div>
    );
  };

  return (
    <>
      <LoadingElement display={isLoading ? "block" : "hidden"} />
      <div
       className={`relative showAnimation min-h-screen flex flex-col  bg-gradient-to-r from-purple-600 via-sky-600 to-blue-900 dark:from-gray-600 dark:via-gray-900 dark:to-black text-white overflow-hidden duration-500`}            >
   
            <div className="absolute  top-0 left-0 w-full h-full overflow-hidden z-10">
          <div className="diamond"></div>
          <div className="diamond diamond-2"></div>
          <div className="diamond diamond-3"></div>
          <div className="diamond diamond-4"></div>
          <div className="diamond diamond-5"></div>
          <div className="diamond diamond-6 "></div>
        </div>
        <div className='relative p-4 z-30 w-full'>
           {Array.isArray(cards) && cards.length === 0 ? (
          <div className="showAnimation  flex items-center justify-center h-64">
            <p className="text-gray-500 dark:text-gray-400">No cards available. Click the button below to add a new card.</p>
            <div className="bg-gray-100 backdrop-blur-sm bg-opacity-30 dark:bg-opacity-30 dark:bg-gray-800 p-6 rounded-lg shadow-md hover:-translate-y-1 transition-transform duration-1000 flex items-center justify-center">
              <button
                onClick={addCard}
                className="w-full h-full flex items-center justify-center text-2xl"
              >
                <FiPlus size={50} />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 z-20 md:grid-cols-3 lg:grid-cols-4">
            {cards.map((card, index) => (
              <div
                key={card._id}
                className={`bg-gray-100 backdrop-blur-sm bg-opacity-30 dark:bg-opacity-30 dark:bg-gray-800 p-6 rounded-lg shadow-md hover:-translate-y-1 transition-transform duration-1000 ${animate && 'card-animate'}`}
              >
                {editCard && editCard._id === card._id ? (
                 <>
                 <FilePond
                   files={files}
                   onupdatefiles={setFiles}
                  
                   labelIdle='Drag & Drop your image or <span class="filepond--label-action">Browse</span>'
                 />
                 <input
                   type="text"
                   value={editTitle}
                   onChange={(e) => setEditTitle(e.target.value)}
                   className="w-full mt-4 p-2 border border-gray-300 rounded dark:border-gray-700 dark:bg-gray-800 text-black dark:text-white"
                 />
                 <textarea
                   value={editDescription}
                   onChange={(e) => setEditDescription(e.target.value)}
                   className="w-full mt-4 p-2 border border-gray-300 rounded dark:border-gray-700 dark:bg-gray-800 text-black dark:text-white"
                 />
                 <button
                   onClick={() => updateCard(card._id)}
                   className="mt-4 px-4 py-2 bg-blue-500 dark:bg-blue-700 text-white rounded-full hover:bg-blue-600 dark:hover:bg-blue-600"
                 >
                   Save
                 </button>
               </>
               
                ) : (
                  <>
              
                    <img
                      src={`https://my-first-website-production.up.railway.app${card.imgSrc}`}
                      alt={card.title}
                      className="skeleton w-full h-48 object-cover rounded-lg cursor-pointer"
                      onClick={() => handleImageClick(card)}
                    />
                    <h2 className="mt-4 text-2xl font-bold text-gray-800 dark:text-gray-200">
                      {card.title}
                    </h2>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                      {truncateDescription(card.description)}
                    </p>
                    <div className="mt-4 flex justify-center gap-5">
                      <button
                        onClick={() => startEditCard(card)}
                        className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600"
                      >
                        <FiEdit />
                      </button>
                      <button
                        onClick={() => deleteCard(card._id)}
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <FiTrash />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            <div className="bg-gray-100 backdrop-blur-sm bg-opacity-30 dark:bg-opacity-30 dark:bg-gray-800 p-6 rounded-lg shadow-md hover:-translate-y-1 transition-transform duration-1000 flex items-center justify-center">
              <button
                onClick={addCard}
                className="w-full h-full flex items-center justify-center text-2xl"
              >
                <FiPlus size={50} />
              </button>
            </div>
          </div>
        )}
        </div>

        {zoomedImage && (
          <div className="showAnimation fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <button
              className="absolute top-4 right-4 bg-white text-black rounded-full p-2 focus:outline-none"
              onClick={closeZoom}
            >
                <FaTimes />
                </button>
            <img
              src={`https://my-first-website-production.up.railway.app${zoomedImage}`}
              alt="Zoomed"
              id="zoomedImage"
              className="max-w-full max-h-full object-contain shadow-lg"
            />
          </div>
        )}
        <Pagination totalPages={totalPages} currentPage={page} onPageChange={setPage} />
      </div>
    </>
  );
};

export default CardGrid;
